import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import SearchableBankSelect from '../components/SearchableBankSelect';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import api from '../lib/api';
import { Plus, Eye, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { ExportMenu } from '../components/ExportMenu';
import { ImportDialog } from '../components/ImportDialog';


export default function Suppliers() {
  const { user } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchFilter, setSearchFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [formData, setFormData] = useState({
    nama_supplier: '',
    alamat_lengkap: '',
    nomor_npwp: '',
    nama_bank: '',
    nomor_rekening: '',
    nama_pemilik_rekening: '',
    email: '',
    nomor_wa: '',
    nomor_kontak_person: '',
    nama_kontak_person: '',
    uses_virtual_account: false
  });

  // Helper function to update form data with uppercase (except email)
  const updateFormField = (field, value) => {
    if (field === 'email') {
      setFormData({ ...formData, [field]: value.toLowerCase() });
    } else if (field === 'nomor_rekening' || field === 'nomor_kontak_person' || field === 'nomor_wa' || field === 'nomor_npwp') {
      // Keep numbers as-is
      setFormData({ ...formData, [field]: value });
    } else {
      setFormData({ ...formData, [field]: value.toUpperCase() });
    }
  };

  // Helper to extract error message from API response
  const getErrorMessage = (error, defaultMsg) => {
    const detail = error.response?.data?.detail;
    if (!detail) return defaultMsg;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
    }
    if (typeof detail === 'object') {
      return detail.msg || detail.message || JSON.stringify(detail);
    }
    return defaultMsg;
  };

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await api.get(`/suppliers`);
      setSuppliers(response.data);
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
      // Don't show error toast if it's just empty data
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/suppliers`, formData);
      toast.success('Supplier berhasil ditambahkan!');
      setShowCreate(false);
      resetForm();
      fetchSuppliers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Gagal menambahkan supplier'));
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/suppliers/${selectedSupplier.id}`, formData);
      toast.success('Supplier berhasil diupdate!');
      setShowEdit(false);
      resetForm();
      fetchSuppliers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Gagal update supplier'));
    }
  };

  const handleDelete = async (supplierId) => {
    if (!window.confirm('Yakin ingin menghapus supplier ini?')) return;
    try {
      await api.delete(`/suppliers/${supplierId}`);
      toast.success('Supplier berhasil dihapus!');
      fetchSuppliers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Gagal menghapus supplier'));
    }
  };

  const resetForm = () => {
    setFormData({
      nama_supplier: '',
      alamat_lengkap: '',
      nomor_npwp: '',
      nama_bank: '',
      nomor_rekening: '',
      nama_pemilik_rekening: '',
      email: '',
      nomor_wa: '',
      nomor_kontak_person: '',
      nama_kontak_person: '',
      uses_virtual_account: false
    });
  };

  const openEdit = (supplier) => {
    setSelectedSupplier(supplier);
    setFormData({
      nama_supplier: supplier.nama_supplier,
      alamat_lengkap: supplier.alamat_lengkap,
      nomor_npwp: supplier.nomor_npwp,
      nama_bank: supplier.nama_bank || '',
      nomor_rekening: supplier.nomor_rekening,
      nama_pemilik_rekening: supplier.nama_pemilik_rekening,
      email: supplier.email,
      nomor_wa: supplier.nomor_wa || '',
      nomor_kontak_person: supplier.nomor_kontak_person || '',
      nama_kontak_person: supplier.nama_kontak_person || '',
      uses_virtual_account: supplier.uses_virtual_account || false
    });
    setShowEdit(true);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="suppliers-page">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Database Supplier</h1>
          <div className="flex gap-2">
            <ExportMenu table="suppliers" size="default" />
            {['purchasing', 'admin'].includes(user?.role) && <ImportDialog type="suppliers" title="Supplier" size="default" onDone={fetchSuppliers} />}
            <Button
              variant="outline"
              onClick={fetchSuppliers}
              data-testid="refresh-suppliers-button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/></svg>
              Refresh
            </Button>
            {['purchasing', 'admin'].includes(user?.role) && (
              <Dialog open={showCreate} onOpenChange={setShowCreate}>
              <DialogTrigger asChild>
                <Button data-testid="create-supplier-button" className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="mr-2 h-4 w-4" /> Tambah Supplier
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Tambah Supplier Baru</DialogTitle>
                  <p className="text-sm text-slate-600">Semua field wajib diisi</p>
                </DialogHeader>
                <form onSubmit={handleCreate} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="nama_supplier">Nama Supplier *</Label>
                      <Input
                        id="nama_supplier"
                        value={formData.nama_supplier}
                        onChange={(e) => updateFormField('nama_supplier', e.target.value)}
                        style={{ textTransform: 'uppercase' }}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => updateFormField('email', e.target.value)}
                        style={{ textTransform: 'lowercase' }}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="nomor_wa">Nomor WhatsApp</Label>
                    <Input
                      id="nomor_wa"
                      type="tel"
                      pattern="[0-9]{10,15}"
                      maxLength="15"
                      value={formData.nomor_wa}
                      onChange={(e) => updateFormField('nomor_wa', e.target.value.replace(/[^0-9]/g, ''))}
                      placeholder="6281234567890"
                    />
                    <p className="text-xs text-slate-500">Format: 62 + nomor (contoh: 6281234567890)</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="alamat">Alamat Lengkap *</Label>
                    <textarea
                      id="alamat"
                      className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm uppercase"
                      value={formData.alamat_lengkap}
                      onChange={(e) => updateFormField('alamat_lengkap', e.target.value)}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="npwp">Nomor NPWP *</Label>
                      <Input
                        id="npwp"
                        value={formData.nomor_npwp}
                        onChange={(e) => updateFormField('nomor_npwp', e.target.value)}
                        required
                      />
                    </div>
                    <SearchableBankSelect
                      value={formData.nama_bank}
                      onChange={(bankName) => setFormData({ ...formData, nama_bank: bankName })}
                      label="Nama Bank"
                      required={true}
                      id="bank"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="rekening">Nomor Rekening *</Label>
                      <Input
                        id="rekening"
                        value={formData.nomor_rekening}
                        onChange={(e) => updateFormField('nomor_rekening', e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pemilik">Nama Pemilik Rekening *</Label>
                      <Input
                        id="pemilik"
                        value={formData.nama_pemilik_rekening}
                        onChange={(e) => updateFormField('nama_pemilik_rekening', e.target.value)}
                        style={{ textTransform: 'uppercase' }}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="nomor_kontak">Nomor Kontak Person * (10-13 digit)</Label>
                      <Input
                        id="nomor_kontak"
                        type="tel"
                        pattern="[0-9]{10,13}"
                        maxLength="13"
                        value={formData.nomor_kontak_person}
                        onChange={(e) => updateFormField('nomor_kontak_person', e.target.value.replace(/[^0-9]/g, ''))}
                        placeholder="08123456789"
                        required
                      />
                      <p className="text-xs text-slate-500">Min 10 digit, Max 13 digit</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="nama_kontak">Nama Kontak Person *</Label>
                      <Input
                        id="nama_kontak"
                        type="text"
                        value={formData.nama_kontak_person}
                        onChange={(e) => updateFormField('nama_kontak_person', e.target.value)}
                        style={{ textTransform: 'uppercase' }}
                        placeholder="JOHN DOE"
                        required
                      />
                    </div>
                  </div>

                  {/* Virtual Account Checkbox */}
                  <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <input
                      type="checkbox"
                      id="uses_va"
                      checked={formData.uses_virtual_account}
                      onChange={(e) => setFormData({ ...formData, uses_virtual_account: e.target.checked })}
                      className="h-4 w-4 rounded border-amber-400 text-red-600 focus:ring-red-500"
                      data-testid="uses-va-checkbox"
                    />
                    <Label htmlFor="uses_va" className="cursor-pointer text-amber-800 text-sm">
                      Supplier menggunakan <strong>Virtual Account</strong> untuk pembayaran
                    </Label>
                  </div>

                  <Button type="submit" className="w-full" data-testid="submit-supplier-button">
                    Simpan Supplier
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            )}
          </div>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Daftar Supplier</CardTitle>
            <Input
              placeholder="Cari nama supplier..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="max-w-xs"
              data-testid="search-supplier"
            />
          </CardHeader>
          <CardContent>
            {(() => {
              // Sort by name after PT/CV prefix, then filter
              const sortKey = (name) => {
                const n = (name || '').toUpperCase();
                return n.replace(/^(PT\.?\s*|CV\.?\s*)/i, '').trim();
              };
              const sorted = [...suppliers].sort((a, b) => sortKey(a.nama_supplier).localeCompare(sortKey(b.nama_supplier)));
              const filtered = sorted.filter(s => !searchFilter || s.nama_supplier?.toLowerCase().includes(searchFilter.toLowerCase()));
              return filtered.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 text-left">
                      <th className="px-4 py-3 text-sm font-semibold">Nama Supplier</th>
                      <th className="px-4 py-3 text-sm font-semibold">Email</th>
                      <th className="px-4 py-3 text-sm font-semibold">Bank</th>
                      <th className="px-4 py-3 text-sm font-semibold">Nama Kontak</th>
                      <th className="px-4 py-3 text-sm font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((supplier) => (
                      <tr key={supplier.id} className="border-b">
                        <td className="px-4 py-3 font-semibold">{supplier.nama_supplier}</td>
                        <td className="px-4 py-3">{supplier.email}</td>
                        <td className="px-4 py-3 text-sm">{supplier.nama_bank || '-'}</td>
                        <td className="px-4 py-3">{supplier.nama_kontak_person || '-'}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setSelectedSupplier(supplier); setShowDetail(true); }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {['purchasing', 'admin'].includes(user?.role) && (
                              <>
                                <Button size="sm" variant="outline" onClick={() => openEdit(supplier)}>
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="destructive" onClick={() => handleDelete(supplier.id)}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">{searchFilter ? 'Tidak ada hasil pencarian' : 'Belum ada supplier'}</p>
            );
            })()}
          </CardContent>
        </Card>

        <Dialog open={showDetail} onOpenChange={setShowDetail}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Detail Supplier</DialogTitle>
              <p className="text-sm text-slate-600">Informasi lengkap supplier</p>
            </DialogHeader>
            {selectedSupplier && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-600">Nama Supplier</p>
                    <p className="font-semibold">{selectedSupplier.nama_supplier}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Email</p>
                    <p className="font-semibold">{selectedSupplier.email}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-slate-600">Alamat Lengkap</p>
                    <p className="text-sm">{selectedSupplier.alamat_lengkap}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Nomor NPWP</p>
                    <p className="font-mono">{selectedSupplier.nomor_npwp}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Bank</p>
                    <p className="font-semibold">{selectedSupplier.nama_bank || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Nomor Rekening</p>
                    <p className="font-mono">{selectedSupplier.nomor_rekening}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Nama Pemilik Rekening</p>
                    <p>{selectedSupplier.nama_pemilik_rekening}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Nomor Kontak Person</p>
                    <p className="font-mono">{selectedSupplier.nomor_kontak_person || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Nama Kontak Person</p>
                    <p>{selectedSupplier.nama_kontak_person || '-'}</p>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={showEdit} onOpenChange={setShowEdit}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Supplier</DialogTitle>
              <p className="text-sm text-slate-600">Update data supplier</p>
            </DialogHeader>
            <form onSubmit={handleEdit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_nama_supplier">Nama Supplier *</Label>
                  <Input
                    id="edit_nama_supplier"
                    value={formData.nama_supplier}
                    onChange={(e) => setFormData({ ...formData, nama_supplier: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_email">Email *</Label>
                  <Input
                    id="edit_email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_alamat">Alamat Lengkap *</Label>
                <textarea
                  id="edit_alamat"
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={formData.alamat_lengkap}
                  onChange={(e) => setFormData({ ...formData, alamat_lengkap: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_npwp">Nomor NPWP *</Label>
                  <Input
                    id="edit_npwp"
                    value={formData.nomor_npwp}
                    onChange={(e) => setFormData({ ...formData, nomor_npwp: e.target.value })}
                    required
                  />
                </div>
                <SearchableBankSelect
                  value={formData.nama_bank}
                  onChange={(bankName) => setFormData({ ...formData, nama_bank: bankName })}
                  label="Nama Bank"
                  required={true}
                  id="edit_bank"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_rekening">Nomor Rekening *</Label>
                  <Input
                    id="edit_rekening"
                    value={formData.nomor_rekening}
                    onChange={(e) => setFormData({ ...formData, nomor_rekening: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_pemilik">Nama Pemilik Rekening *</Label>
                  <Input
                    id="edit_pemilik"
                    value={formData.nama_pemilik_rekening}
                    onChange={(e) => setFormData({ ...formData, nama_pemilik_rekening: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_nomor_kontak">Nomor Kontak Person * (10-13 digit)</Label>
                  <Input
                    id="edit_nomor_kontak"
                    type="tel"
                    pattern="[0-9]{10,13}"
                    maxLength="13"
                    value={formData.nomor_kontak_person}
                    onChange={(e) => setFormData({ ...formData, nomor_kontak_person: e.target.value.replace(/[^0-9]/g, '') })}
                    placeholder="08123456789"
                    required
                  />
                  <p className="text-xs text-slate-500">Min 10 digit, Max 13 digit</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_nama_kontak">Nama Kontak Person *</Label>
                  <Input
                    id="edit_nama_kontak"
                    type="text"
                    value={formData.nama_kontak_person}
                    onChange={(e) => setFormData({ ...formData, nama_kontak_person: e.target.value })}
                    placeholder="John Doe"
                    required
                  />
                </div>
              </div>

              <Button type="submit" className="w-full">
                Update Supplier
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}



